import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { JobTable, JobFilter, Pagination, JobFormModal, JobApplicantsModal } from '../components';
import { fetchJobs, createJob, updateJob, deleteJob } from '../services/api';

const JobManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState({
    title: '',
    companyName: '',
    category: '',
    subcategory: '',
    status: '',
    location: '',
    jobType: '',
    isActive: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApplicantsModal, setShowApplicantsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  const fetchJobsData = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page,
        size: 10,
        sortBy: 'createdAt',
        sortDirection: 'desc'
      };
      
      const data = await fetchJobs(params);
      setJobs(data.content || []);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.page || 0);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch jobs. Please try again.');
      setJobs([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobsData();
  }, [filters]);

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      fetchJobsData(newPage);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const handleCreateJob = async (jobData) => {
    setLoading(true);
    try {
      await createJob(jobData);
      toast.success('Job created successfully!');
      setShowCreateModal(false);
      fetchJobsData(0); // Refresh and go to first page
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error('Failed to create job. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateJob = async (jobId, jobData) => {
    setLoading(true);
    try {
      await updateJob(jobId, jobData);
      toast.success('Job updated successfully!');
      fetchJobsData(); // Refresh current page
    } catch (error) {
      console.error('Error updating job:', error);
      
      if (error.response && error.response.status === 400) {
        const errorMessage = error.response.data.message;
        toast.error(errorMessage);
      } else {
        toast.error('Failed to update job. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      setLoading(true);
      try {
        await deleteJob(jobId);
        toast.success('Job deleted successfully!');
        fetchJobsData(); // Refresh current page
      } catch (error) {
        console.error('Error deleting job:', error);
        
        if (error.response && error.response.status === 400) {
          const errorMessage = error.response.data.message;
          toast.error(errorMessage);
        } else {
          toast.error('Failed to delete job. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewApplicants = (job) => {
    setSelectedJob(job);
    setShowApplicantsModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Job Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Create Job
        </button>
      </div>

      <JobFilter onFilterChange={handleFilterChange} filters={filters} />

      <JobTable 
        jobs={jobs} 
        loading={loading}
        onUpdate={handleUpdateJob}
        onDelete={handleDeleteJob}
        fetchJobs={fetchJobsData}
        onViewApplicants={handleViewApplicants}
      />

      {!loading && jobs.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      
      <div className="mt-6 text-center text-sm text-gray-500">
        {!loading && jobs.length > 0 && (
          <p>Showing {jobs.length} of {totalElements} jobs</p>
        )}
        {!loading && jobs.length === 0 && (
          <p>No jobs found</p>
        )}
      </div>

      {showCreateModal && (
        <JobFormModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateJob}
        />
      )}

      {showApplicantsModal && selectedJob && (
        <JobApplicantsModal
          job={selectedJob}
          onClose={() => {
            setShowApplicantsModal(false);
            setSelectedJob(null);
          }}
        />
      )}
    </div>
  );
};

export default JobManagement; 